import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { onboardingAdmin as rules } from 'further-ui/validations/';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import { styled, Button, Box } from '@mui/material';
import InvestorAddressList from 'components/new/InvestorAddressList';
import { useEffect, useMemo } from 'react';
import { InvestorAddress } from '../../types';
import { Country } from 'further-ui/labels';
import { CalendarDay } from 'further-ui/utils';
import TextField from '../../StyledComponents/TextField';
import DatePicker from '../../StyledComponents/DatePicker';
import CountrySelector from '../../StyledComponents/CountrySelector';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';

const ButtonContainer = styled(Box)({
  textAlign: 'right',
});

type Props = {
  vehicleId: string;
  vehicleApplicationId: string;
  investor: {
    id: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    phoneNumber?: string;
    dateOfBirth?: CalendarDay;
    jobTitle?: string;
    sector?: string;
    addresses: Array<InvestorAddress>;
    nationalities: Array<Country>;
  };
  isActive: boolean;
  onSave: () => void;
};

const IndividualInvestor: React.FC<Props> = ({
  vehicleId,
  vehicleApplicationId,
  isActive,
  investor,
  onSave,
}) => {
  type FormValues = z.infer<typeof rules.SaveIndividualInvestorDetailsInput>;

  const { saveIndividualInvestorDetails, isPending } = useVehicleApplication(
    vehicleId,
    vehicleApplicationId,
  );

  const defaultValues = useMemo(() => {
    return {
      investorId: investor.id,
      firstName: investor.firstName,
      middleName: investor.middleName ?? '',
      lastName: investor.lastName,
      dateOfBirth: investor.dateOfBirth,
      phoneNumber: investor.phoneNumber ?? '',
      jobTitle: investor.jobTitle ?? '',
      sector: investor.sector ?? '',
      addresses: investor.addresses.length
        ? investor.addresses
        : [
            {
              address1: '',
              city: '',
              postcode: '',
              country: undefined,
              hasBeenUsedForThreeYearsOrMore: true,
            },
          ],
      nationalities: investor.nationalities,
    };
  }, [investor]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.SaveIndividualInvestorDetailsInput),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [isActive, reset, defaultValues]);

  const onSubmit = (values: FormValues) => {
    if (isPending) {
      return;
    }
    saveIndividualInvestorDetails(
      {
        vehicleId,
        ...values,
      },
      {
        onSuccess: onSave,
      },
    );
  };

  return (
    <Column fullWidth spacing="lg">
      <FormField label="First name">
        <Controller
          name="firstName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="First name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Middle name">
        <Controller
          name="middleName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Middle name (optional)"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Last name">
        <Controller
          name="lastName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Last name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Birth date">
        <Controller
          name="dateOfBirth"
          disabled={!isActive}
          control={control}
          render={({
            field: { value, onChange, name },
            fieldState: { error },
          }) => (
            <DatePicker
              fullWidth
              placeholder="Birth date"
              disabled={!isActive}
              value={value}
              name={name}
              onChange={(value) => {
                onChange(value ?? undefined);
              }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Phone number">
        <Controller
          name="phoneNumber"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Phone number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Nationality">
        <Controller
          name="nationalities"
          disabled={!isActive}
          control={control}
          render={({
            field: { value, name, onChange },
            fieldState: { error },
          }) => (
            <CountrySelector
              placeholder="Nationality"
              disabled={!isActive}
              value={value[0]}
              name={name}
              onChange={(country) => {
                onChange(country ? [country] : []);
              }}
              error={error}
            />
          )}
        />
      </FormField>
      <InvestorAddressList
        addressFieldName="addresses"
        //@ts-expect-error TODO: Improve the `control` type inside InvestorAddressList
        control={control}
        disabled={!isActive}
        slots={{
          TextField,
          CountrySelector,
        }}
      />
      <FormField label="Job title">
        <Controller
          name="jobTitle"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Job title"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Sector">
        <Controller
          name="sector"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Sector"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      {isActive ? (
        <ButtonContainer>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={isPending}
          >
            Save
          </Button>
        </ButtonContainer>
      ) : null}
    </Column>
  );
};

export default IndividualInvestor;
